import React from 'react';
import PropTypes from 'prop-types';
import { ROLE_PSM } from 'constants/department';
import Info from './Info';
import FeaturePermission from './FeaturePermission';
import Confirm from './Confirm';

const STEP = {
  INFO: 'INFO',
  FEATURE_PERMISSION: 'FEATURE_PERMISSION',
  CONFIRM: 'CONFIRM',
};

function ModalAddInternalAccount(props) {
  const {
    isOpen,
    onToggleModal,
    marketList,
    marketTotal,
    isLoadingMarketList,
    actionGetMarketList,
    actionCreateInternalAccount,
    isLoadingCreateInternalAccount,
    editAccountId,
    internalAccountDetail,
    actionGetInternalAccountDetail,
    actionResetInternalAccountDetail,
    actionUpdateInternalAccount,
    isLoadingUpdateInternalAccount,
    onRefreshList,
  } = props;
  const [step, setStep] = React.useState(STEP.INFO);
  const [accountInfo, setAccountInfo] = React.useState({});

  const onNextFromInfo = React.useCallback((values) => {
    setAccountInfo(values);
    setStep(STEP.FEATURE_PERMISSION);
  }, []);

  const onNextFromFeaturePermission = React.useCallback((featurePermissionValues) => {
    setAccountInfo((prev) => {
      const next = { ...prev, ...featurePermissionValues };

      if (prev.department !== ROLE_PSM) {
        delete next.markets;
        delete next.market;
      }

      return next;
    });
    setStep(STEP.CONFIRM);
  }, []);

  React.useEffect(() => {
    if (isOpen) {
      setStep(STEP.INFO);
      if (editAccountId) {
        actionGetInternalAccountDetail(editAccountId);
      } else {
        setAccountInfo({});
      }
    } else {
      actionResetInternalAccountDetail();
    }
  }, [isOpen, editAccountId, actionGetInternalAccountDetail, actionResetInternalAccountDetail]);

  React.useEffect(() => {
    if (editAccountId && internalAccountDetail) {
      setAccountInfo(internalAccountDetail);
    }
  }, [internalAccountDetail, editAccountId]);

  return (
    <div>
      <Info
        isOpen={isOpen && step === STEP.INFO}
        onToggleModal={onToggleModal}
        onNext={onNextFromInfo}
        accountInfo={accountInfo}
        editAccountId={editAccountId}
      />
      <FeaturePermission
        isOpen={isOpen && step === STEP.FEATURE_PERMISSION}
        onToggleModal={onToggleModal}
        onNext={onNextFromFeaturePermission}
        accountInfo={accountInfo}
        marketList={marketList}
        marketTotal={marketTotal}
        isLoadingMarketList={isLoadingMarketList}
        actionGetMarketList={actionGetMarketList}
      />
      <Confirm
        isOpen={isOpen && step === STEP.CONFIRM}
        onToggleModal={onToggleModal}
        accountInfo={accountInfo}
        actionCreateInternalAccount={actionCreateInternalAccount}
        isLoadingCreateInternalAccount={isLoadingCreateInternalAccount}
        editAccountId={editAccountId}
        actionUpdateInternalAccount={actionUpdateInternalAccount}
        isLoadingUpdateInternalAccount={isLoadingUpdateInternalAccount}
        onRefreshList={onRefreshList}
      />
    </div>
  );
}

ModalAddInternalAccount.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onToggleModal: PropTypes.func.isRequired,
  marketList: PropTypes.instanceOf(Array),
  marketTotal: PropTypes.number,
  isLoadingMarketList: PropTypes.bool,
  actionGetMarketList: PropTypes.func,
  actionCreateInternalAccount: PropTypes.func,
  isLoadingCreateInternalAccount: PropTypes.bool,
  editAccountId: PropTypes.string,
  internalAccountDetail: PropTypes.instanceOf(Object),
  actionGetInternalAccountDetail: PropTypes.func,
  actionResetInternalAccountDetail: PropTypes.func,
  actionUpdateInternalAccount: PropTypes.func,
  isLoadingUpdateInternalAccount: PropTypes.bool,
  onRefreshList: PropTypes.func,
};

ModalAddInternalAccount.defaultProps = {
  marketList: [],
  marketTotal: 0,
  isLoadingMarketList: false,
  actionGetMarketList: () => {},
  actionCreateInternalAccount: () => {},
  isLoadingCreateInternalAccount: false,
  editAccountId: null,
  internalAccountDetail: {},
  actionGetInternalAccountDetail: () => {},
  actionResetInternalAccountDetail: () => {},
  actionUpdateInternalAccount: () => {},
  isLoadingUpdateInternalAccount: false,
  onRefreshList: () => {},
};

export default React.memo(ModalAddInternalAccount);
